import { useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import { useAuth } from '../../context/AuthContext';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface WithdrawModalProps {
  onClose: () => void;
}

export default function WithdrawModal({ onClose }: WithdrawModalProps) {
  const { member, logout } = useAuth();
  const [step, setStep] = useState<'confirm' | 'verify' | 'done'>('confirm');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [reason, setReason] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const reasons = [
    '서비스를 더 이상 이용하지 않아요',
    '개인정보 보호가 걱정돼요',
    '다른 서비스로 이동할 예정이에요',
    '서비스에 불만족해요',
    '기타',
  ];

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) {
      setErrorMsg('비밀번호를 입력해 주세요.');
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      const { data, error } = await supabase
        .from('members')
        .select('password_hash, phone')
        .eq('member_id', member!.member_id)
        .maybeSingle();

      if (error || !data) {
        setErrorMsg('회원 정보를 확인할 수 없습니다.');
        setStatus('error');
        return;
      }

      let isValid = false;
      if (data.password_hash) {
        isValid = data.password_hash === password;
      } else {
        isValid = data.phone?.replace(/-/g, '') === password.replace(/-/g, '');
      }

      if (!isValid) {
        setErrorMsg('비밀번호가 올바르지 않습니다.');
        setStatus('error');
        return;
      }

      // 탈퇴 처리
      const { error: updateError } = await supabase
        .from('members')
        .update({
          is_withdrawn: true,
          withdrawn_at: new Date().toISOString(),
        })
        .eq('member_id', member!.member_id);

      if (updateError) {
        setErrorMsg('탈퇴 처리 중 오류가 발생했습니다. 다시 시도해 주세요.');
        setStatus('error');
        return;
      }

      setStep('done');
      setStatus('idle');
    } catch {
      setErrorMsg('오류가 발생했습니다. 다시 시도해 주세요.');
      setStatus('error');
    }
  };

  const handleDone = () => {
    logout();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      onClick={(e) => { if (e.target === e.currentTarget && step !== 'done') onClose(); }}
    >
      <div className="bg-white rounded-2xl w-full max-w-sm p-8 relative">
        {step !== 'done' && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 cursor-pointer"
          >
            <i className="ri-close-line text-xl w-5 h-5 flex items-center justify-center"></i>
          </button>
        )}

        {/* STEP 1: 탈퇴 안내 */}
        {step === 'confirm' && (
          <>
            <div className="text-center mb-6">
              <div className="w-14 h-14 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-3">
                <i className="ri-user-unfollow-line text-red-500 text-2xl w-7 h-7 flex items-center justify-center"></i>
              </div>
              <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                회원 탈퇴
              </h2>
              <p className="text-sm text-gray-500 mt-1.5">
                <span className="font-semibold text-gray-700">{member?.member_name}</span> 님, 정말 탈퇴하시겠어요?
              </p>
            </div>

            {/* 탈퇴 시 유의사항 */}
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-5">
              <p className="text-xs font-bold text-red-600 mb-2 flex items-center gap-1.5">
                <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center"></i>
                탈퇴 시 유의사항
              </p>
              <ul className="flex flex-col gap-1.5">
                {[
                  '보유 포인트가 모두 소멸됩니다',
                  '주문 내역 및 적립 내역이 삭제됩니다',
                  '탈퇴 후 동일 ID로 재가입이 불가합니다',
                  '탈퇴 처리는 즉시 적용되며 복구가 불가합니다',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-1.5 text-xs text-red-700">
                    <i className="ri-checkbox-blank-circle-fill text-red-400 mt-0.5 flex-shrink-0" style={{ fontSize: '6px' }}></i>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* 현재 포인트 표시 */}
            {(member?.points_balance ?? 0) > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5 flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                  <i className="ri-coin-line text-amber-600 text-sm w-4 h-4 flex items-center justify-center"></i>
                </div>
                <div>
                  <p className="text-xs text-amber-700 font-semibold">소멸 예정 포인트</p>
                  <p className="text-base font-bold text-amber-600">
                    {(member?.points_balance ?? 0).toLocaleString()}P
                  </p>
                </div>
              </div>
            )}

            {/* 탈퇴 사유 선택 */}
            <div className="mb-5">
              <p className="text-sm font-semibold text-gray-700 mb-2">탈퇴 사유 (선택)</p>
              <div className="flex flex-col gap-1.5">
                {reasons.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setReason(reason === r ? '' : r)}
                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border text-sm text-left transition-all cursor-pointer ${
                      reason === r
                        ? 'border-red-400 bg-red-50 text-red-700 font-medium'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <span className={`w-4 h-4 flex items-center justify-center flex-shrink-0 rounded-full border-2 transition-all ${
                      reason === r ? 'border-red-500 bg-red-500' : 'border-gray-300'
                    }`}>
                      {reason === r && (
                        <i className="ri-check-line text-white" style={{ fontSize: '10px' }}></i>
                      )}
                    </span>
                    {r}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="flex-1 py-3 rounded-lg border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                취소
              </button>
              <button
                onClick={() => setStep('verify')}
                className="flex-1 py-3 rounded-lg bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                탈퇴 진행
              </button>
            </div>
          </>
        )}

        {/* STEP 2: 비밀번호 확인 */}
        {step === 'verify' && (
          <>
            <div className="text-center mb-6">
              <div className="w-14 h-14 flex items-center justify-center bg-orange-100 rounded-full mx-auto mb-3">
                <i className="ri-shield-keyhole-line text-orange-500 text-2xl w-7 h-7 flex items-center justify-center"></i>
              </div>
              <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                본인 확인
              </h2>
              <p className="text-sm text-gray-500 mt-1.5">
                탈퇴를 위해 비밀번호를 입력해 주세요
              </p>
            </div>

            <form onSubmit={handleVerify} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-gray-700">
                  비밀번호 <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                    <i className="ri-lock-line text-base"></i>
                  </span>
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setErrorMsg(''); setStatus('idle'); }}
                    placeholder="비밀번호 입력"
                    className="w-full pl-10 pr-11 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 transition-all"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <i className={`${showPw ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`}></i>
                  </button>
                </div>
              </div>

              {errorMsg && (
                <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
                  <span className="w-4 h-4 flex items-center justify-center text-red-500 flex-shrink-0">
                    <i className="ri-error-warning-line text-sm"></i>
                  </span>
                  <p className="text-xs text-red-600">{errorMsg}</p>
                </div>
              )}

              <div className="flex gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setStep('confirm')}
                  className="flex-1 py-3 rounded-lg border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  이전
                </button>
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="flex-1 py-3 rounded-lg bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {status === 'loading' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>
                      처리 중...
                    </>
                  ) : (
                    '탈퇴 확인'
                  )}
                </button>
              </div>
            </form>
          </>
        )}

        {/* STEP 3: 탈퇴 완료 */}
        {step === 'done' && (
          <div className="text-center py-4">
            <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
              <i className="ri-checkbox-circle-line text-gray-500 text-3xl w-8 h-8 flex items-center justify-center"></i>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Noto Serif KR, serif' }}>
              탈퇴가 완료되었습니다
            </h2>
            <p className="text-sm text-gray-500 mb-1">
              그동안 <span className="font-semibold text-gray-700">콩이사랑</span>을 이용해 주셔서 감사합니다.
            </p>
            <p className="text-xs text-gray-400 mb-7">
              더 좋은 서비스로 다시 만날 수 있기를 바랍니다.
            </p>
            <button
              onClick={handleDone}
              className="w-full py-3.5 bg-gray-800 text-white rounded-lg text-sm font-semibold hover:bg-gray-900 transition-colors cursor-pointer whitespace-nowrap"
            >
              확인
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
